import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

export function useColaPacientes({ citas, idMedicoLogueado, fetchAgendaDiaria, esVistaMultiSala }) {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // 1. Normalización de horas
    const citasProcesadas = useMemo(() => {
        return (citas || []).map(cita => {
            let horaCalculada = cita.hora;
            if (!horaCalculada && cita.fecha) {
                const d = new Date(cita.fecha);
                horaCalculada = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            }
            return {
                ...cita,
                hora: horaCalculada || '--:--',
                estado: cita.estado || 'pendiente'
            };
        });
    }, [citas]);

    // 2. Filtrado de la cola activa (pendientes y en curso)
    const citasDelFlujo = useMemo(() => {
        return citasProcesadas.filter(c => {
            const profId = c.profesional?.id || c.profesional?._id || c.profesional;
            const esSuCita = String(profId) === String(idMedicoLogueado);
            return esSuCita && ['pendiente', 'en_curso'].includes(c.estado);
        }).sort((a, b) => (a.hora || '00:00').localeCompare(b.hora || '00:00'));
    }, [citasProcesadas, idMedicoLogueado]);

    // 3. Filtrado de pacientes ya atendidos
    const pacientesAtendidos = useMemo(() => {
        return citasProcesadas.filter(c => {
            const profId = c.profesional?.id || c.profesional?._id || c.profesional;
            const esSuCita = String(profId) === String(idMedicoLogueado);
            return esSuCita && c.estado === 'completada';
        }).sort((a, b) => (a.hora || '00:00').localeCompare(b.hora || '00:00'));
    }, [citasProcesadas, idMedicoLogueado]);

    // 4. Segmentación de roles en la cola
    const pacienteActual = citasDelFlujo.find(c => c.estado === 'en_curso');
    const pacientesEnEspera = citasDelFlujo.filter(c => c.estado !== 'en_curso');
    const siguientePaciente = pacientesEnEspera[0];
    const restoDeLaCola = pacientesEnEspera.slice(1);

    // 5. Cálculos de estado y tiempo
    const medicoOcupado = Boolean(pacienteActual);
    const ahora = new Date();
    const isSiguienteEnHora = siguientePaciente && new Date(siguientePaciente.fecha) <= ahora;

    // =========================================================
    // ACCIONES MUTABLES (API)
    // =========================================================
    const handleIniciarConsulta = async (citaId) => {
        try {
            await api.patch(`/consultas/${citaId}/estado`, { estado: 'en_curso' });
            if (!esVistaMultiSala) {
                navigate(`/workspace/${citaId}`);
            } else {
                if (fetchAgendaDiaria) fetchAgendaDiaria(true);
            }
        } catch (error) {
            showToast(error.message || 'Error al iniciar la consulta', 'error');
        }
    };

    const handleCambiarEstado = async (citaId, nuevoEstado) => {
        try {
            await api.patch(`/consultas/${citaId}/estado`, { estado: nuevoEstado });

            if (nuevoEstado === 'ausente') {
                showToast('Paciente marcado como ausente. Pasando al siguiente.', 'info');
            } else if (nuevoEstado === 'completada') {
                showToast('Consulta finalizada. Box liberado.', 'success');
            }

            if (fetchAgendaDiaria) fetchAgendaDiaria(true);
        } catch (error) {
            showToast(error.message || 'Error al actualizar el estado de la cita', 'error');
        }
    };

    // 🎟️ Marcar ausente SÍ gasta la sesión del bono (bono.service._usos cuenta las
    // citas en estado 'ausente'). Como es irreversible de hecho para el saldo del
    // paciente, se pide confirmación, y si la cita lleva bono se dice sin rodeos.
    const handleMarcarAusente = async (cita) => {
        const citaId = cita?.id || cita?._id;
        if (!citaId) return;
        const aviso = cita?.bono
            ? '¿Marcar al paciente como AUSENTE?\n\n'
              + '⚠️ Esta cita va con BONO: la sesión se dará por GASTADA y no se le devolverá al paciente.\n\n'
              + 'Si avisó de que no venía, pulsa "Cancelar" en su lugar: así conserva la sesión.'
            : '¿Marcar al paciente como AUSENTE?\n\n'
              + 'Queda registrado como falta de asistencia.\n\n'
              + 'Si avisó de que no venía, pulsa "Cancelar" en su lugar.';
        if (!window.confirm(aviso)) return;
        await handleCambiarEstado(citaId, 'ausente');
    };

    // 🎟️ Cancelar NO es lo mismo que marcar ausente. Una cita cancelada no cuenta
    // como sesión consumida del bono; una ausencia sí (bono.service._usos). Si el
    // paciente avisa de que no viene, lo correcto es cancelar: le devuelve la sesión.
    const handleCancelarCita = async (citaId) => {
        const aviso = '¿Cancelar esta cita?\n\n'
            + 'Se libera el hueco de la agenda y, si la cita iba con bono, la sesión vuelve al paciente.\n\n'
            + 'Si el paciente no se presentó y quieres que la sesión cuente como gastada, usa "Ausente".';
        if (!window.confirm(aviso)) return;
        try {
            await api.patch(`/consultas/${citaId}/estado`, { estado: 'cancelada' });
            showToast('Cita cancelada. Si iba con bono, la sesión vuelve a estar disponible.', 'success');
            if (fetchAgendaDiaria) fetchAgendaDiaria(true);
        } catch (error) {
            showToast(error.message || 'No se pudo cancelar la cita', 'error');
        }
    };

    const getIniciales = (nombre = '', apellidos = '') => {
        return `${nombre?.charAt(0) || ''}${apellidos?.charAt(0) || ''}`.toUpperCase() || 'P';
    };

    return {
        pacienteActual,
        siguientePaciente,
        restoDeLaCola,
        pacientesAtendidos,
        citasDelFlujo,
        medicoOcupado,
        isSiguienteEnHora,
        handleIniciarConsulta,
        handleCambiarEstado,
        handleMarcarAusente,
        handleCancelarCita,
        getIniciales
    };
}